import { makeStyles } from '@material-ui/core/styles'
import {
  OUTLINED_INPUT_BORDER,
  OUTLINED_INPUT_BORDER_HOVER,
  OUTLINED_INPUT_BORDER_RADIUS,
  CALLOUT_FOREGROUND,
} from 'lib/form-field-styles'

const FILTER_CONTROL_HEIGHT = 36

export const useStyles = makeStyles(theme => ({
  contentContainer: {
    margin: '2rem auto 6rem',
    maxWidth: 832,
    padding: theme.spacing(0, 2),
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3, 2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2, 1.5),
    },
  },
  cardHeaderWithSearch: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 3, 1.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2, 1.5),
    },
  },
  cardHeaderTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    width: '100%',
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: '1.125rem',
    lineHeight: 1.3,
    color: '#00205B',
  },
  cardDivider: {
    height: 1,
    backgroundColor: '#E0E0E0',
  },
  cardContent: {
    padding: theme.spacing(0, 3),
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2),
    },
  },
  cardHeaderFilterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  searchFieldInHeader: {
    flex: '1 1 auto',
    width: '100%',
    maxWidth: 520,
    minWidth: 200,
    [theme.breakpoints.down('xs')]: {
      maxWidth: 'none',
    },
  },
  searchField: {
    flex: '1 1 auto',
    '& .MuiFormControl-root': {
      height: FILTER_CONTROL_HEIGHT,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
      backgroundColor: '#F3F3F3',
      height: FILTER_CONTROL_HEIGHT,
      minHeight: FILTER_CONTROL_HEIGHT,
      maxHeight: FILTER_CONTROL_HEIGHT,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      '& fieldset': {
        border: 'none',
        borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
      },
      '&:hover': {
        backgroundColor: '#EBF0F2',
      },
      '&.Mui-focused': {
        backgroundColor: '#FFFFFF',
        '& fieldset': {
          border: '1px solid #0053FF',
          borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
        },
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
    },
    '& .MuiOutlinedInput-input': {
      padding: 0,
      paddingRight: theme.spacing(1.25),
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#172024',
      height: '100%',
      lineHeight: 1.25,
      boxSizing: 'border-box',
      '&::placeholder': {
        color: '#6B7C87',
        opacity: 1,
      },
    },
    '& .MuiInputAdornment-root': {
      height: '100%',
      maxHeight: 'none',
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.25),
      alignItems: 'center',
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: theme.spacing(0.5),
    },
  },
  searchIcon: {
    color: '#6B7C87',
    fontSize: '1rem',
  },
  monthFilterControl: {
    height: FILTER_CONTROL_HEIGHT,
    minHeight: FILTER_CONTROL_HEIGHT,
    maxHeight: FILTER_CONTROL_HEIGHT,
    boxSizing: 'border-box',
    borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.25,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    borderColor: OUTLINED_INPUT_BORDER,
    color: CALLOUT_FOREGROUND,
    backgroundColor: theme.palette.common.white,
    padding: `0 ${theme.spacing(1.5)}px`,
    '& .MuiButton-label': {
      textTransform: 'none',
      lineHeight: 1.25,
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
      marginLeft: 0,
      display: 'inherit',
    },
    '&:hover': {
      backgroundColor: '#F4F6F8',
      borderColor: OUTLINED_INPUT_BORDER_HOVER,
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      justifyContent: 'flex-start',
    },
  },
  monthFilterIcon: {
    fontSize: '1rem',
    color: '#6B7C87',
  },
  filterDivider: {
    height: 1,
    backgroundColor: '#E0E0E0',
    margin: theme.spacing(2, -3, 0),
    [theme.breakpoints.down('xs')]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
    },
  },
  clearFiltersButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#00205B',
    padding: theme.spacing(0.5, 1),
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
    },
  },
  emptyStateContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 220,
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    boxSizing: 'border-box',
    '& > div': {
      padding: theme.spacing(2, 2, 0),
    },
    '& .MuiTypography-body2': {
      marginBottom: 0,
    },
  },
  incomeCardContent: {
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
      flexDirection: 'column',
    },
  },
  incomeLabel: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: 1.3,
    color: '#6B7C87',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.75),
  },
  incomeAmount: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#00205B',
  },
  incomeDateRange: {
    color: '#6B7C87',
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  hiddenDatePicker: {
    width: 0,
    height: 0,
    overflow: 'hidden',
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
  },
  rowDivider: {
    marginLeft: theme.spacing(-3),
    marginRight: theme.spacing(-3),
    [theme.breakpoints.down('xs')]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
    },
  },
  transactionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 0),
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
  },
  transactionRowLast: {
    paddingBottom: theme.spacing(2),
  },
  rowMain: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    minWidth: 0,
    flex: '1 1 auto',
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  incomeIconWrap: {
    backgroundColor: '#BEF1BB',
    color: '#00210B',
  },
  refundIconWrap: {
    backgroundColor: '#EBF0F2',
    color: '#172024',
  },
  rowIcon: {
    fontSize: '1.25rem',
  },
  rowCopy: {
    minWidth: 0,
    flex: '1 1 auto',
  },
  rowTitleLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
  },
  payorName: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: '#172024',
  },
  rowMeta: {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: '#6B7C87',
  },
  rowAmount: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#00205B',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  rowAmountRefund: {
    color: '#6B7C87',
  },
  statusChip: {
    borderRadius: 8,
    height: 22,
    fontSize: '0.625rem',
    fontWeight: 500,
    letterSpacing: '0.02em',
  },
  completedChip: {
    backgroundColor: '#BEF1BB',
    color: '#00210B',
  },
  pendingChip: {
    backgroundColor: '#FFE0B2',
    color: '#C77700',
  },
  refundedChip: {
    backgroundColor: '#EBF0F2',
    color: '#6B7C87',
  },
}))
